/**
 * edit-source — pure line-range surgery on a `.ls` source string.
 *
 * These helpers underwrite the Notion-style block editor's add / move / delete
 * actions. They operate on the RAW source text (never on the parsed model),
 * splicing whole lines so that:
 *
 *   - a leaf block occupies exactly one line, and
 *   - a compound block (`@choice { … }`, `@if (…) { … }`, `@option … { … }`,
 *     `@phone`, `@gate`, `@achievement`) occupies its brace-balanced line span
 *     — the opener line down to the line that closes its outermost brace.
 *
 * Design rules (mirroring parse-blocks / emit-block):
 *  - Never throw. Out-of-range indices clamp or no-op.
 *  - Line-oriented and lossless: we only ever move/insert/delete whole lines, so
 *    a round-trip through `parseLsBlocks` after any edit stays valid and stable.
 *  - `\n` is the canonical join (matching `replaceSourceLine`). CRLF input is
 *    tolerated on the way in (split on `/\r?\n/`).
 */
import type { LsBlock } from "./parse-blocks.js";

/** An inclusive 1-based line span `[start, end]`. A leaf has `start === end`. */
export interface LineRange {
  /** 1-based first line of the block. */
  readonly start: number;
  /** 1-based last line of the block (inclusive). */
  readonly end: number;
}

/** Split a source string into lines, tolerant of CRLF. */
function splitLines(source: string): string[] {
  return source.split(/\r?\n/);
}

/** Count occurrences of a single char in a string. */
function countChar(s: string, ch: string): number {
  let n = 0;
  for (const c of s) if (c === ch) n++;
  return n;
}

/**
 * The inclusive 1-based line range a block occupies in `source`.
 *
 * Leaf blocks (dialogue, @bg, @sfx, …) return `{ start, end }` with `start ===
 * end`. Compound blocks — any block whose opener line has more `{` than `}` —
 * extend `end` forward until the brace depth returns to its pre-opener level,
 * i.e. the line that closes the block. If the braces never balance (malformed
 * source) the range extends to the last line, so a delete/move still removes a
 * coherent chunk rather than corrupting a dangling tail.
 *
 * `blocks` is the parsed model for `source`; `index` selects the block. A stale
 * or out-of-range block/line clamps to a single-line range so callers never
 * throw.
 */
export function blockLineRange(source: string, blocks: readonly LsBlock[], index: number): LineRange {
  const lines = splitLines(source);
  const block = blocks[index];
  if (!block) return { start: 1, end: 1 };

  const start = Math.min(Math.max(1, block.line), lines.length);
  const startIdx = start - 1;
  const openerLine = lines[startIdx] ?? "";

  // Net brace delta on the opener line. Leaf lines are brace-neutral (0) — a
  // dialogue/@bg/etc. — so the range is a single line. A compound opener has a
  // positive net delta (opens more braces than it closes).
  let depth = countChar(openerLine, "{") - countChar(openerLine, "}");
  if (depth <= 0) return { start, end: start };

  // Walk forward until the opener's braces are balanced (depth back to 0).
  for (let i = startIdx + 1; i < lines.length; i++) {
    const line = lines[i] ?? "";
    depth += countChar(line, "{") - countChar(line, "}");
    if (depth <= 0) return { start, end: i + 1 };
  }
  // Unbalanced — extend to EOF (tolerant fallback).
  return { start, end: lines.length };
}

/**
 * Insert `text` (which may itself contain `\n` for a multi-line template) so it
 * becomes the block(s) starting at 1-based line `atLine`. Existing content at
 * `atLine` shifts down. `atLine` clamps to `[1, lines.length + 1]`; passing
 * `lines.length + 1` (or any larger value) appends at the end.
 */
export function insertLine(source: string, atLine: number, text: string): string {
  const lines = source === "" ? [] : splitLines(source);
  const insertAt = Math.min(Math.max(1, atLine), lines.length + 1) - 1;
  const inserted = text.split(/\r?\n/);
  lines.splice(insertAt, 0, ...inserted);
  return lines.join("\n");
}

/**
 * Delete the inclusive 1-based line range `[start, end]` from `source`. Ranges
 * are clamped to the document; an empty or fully-out-of-range span is a no-op.
 */
export function deleteLines(source: string, start: number, end: number): string {
  const lines = splitLines(source);
  const from = Math.max(1, Math.min(start, end));
  const to = Math.min(lines.length, Math.max(start, end));
  if (from > lines.length) return source;
  lines.splice(from - 1, to - from + 1);
  return lines.join("\n");
}

/**
 * Move the inclusive 1-based line range `[from.start, from.end]` so its first
 * line lands at the 1-based `to` position (measured in the ORIGINAL document's
 * coordinates — i.e. "put this block where line `to` currently is"). Moving a
 * block onto its own range is a no-op.
 *
 * The chunk is removed first, then re-inserted before whatever line originally
 * sat at `to`. When `to` is past the moved chunk, the removal shifts the target
 * left by the chunk length — this is accounted for so the block lands where the
 * caller expects.
 */
export function moveLines(source: string, from: LineRange, to: number): string {
  const lines = splitLines(source);
  const len = lines.length;
  const start = Math.max(1, from.start);
  const end = Math.min(len, from.end);
  if (start > len || start > end) return source;

  const count = end - start + 1;
  // No-op: target sits inside the moved chunk (or exactly at its start).
  if (to >= start && to <= end + 1) return source;

  // Extract the chunk.
  const chunk = lines.slice(start - 1, end);
  const rest = [...lines.slice(0, start - 1), ...lines.slice(end)];

  // Translate the original 1-based `to` into an index in `rest`. Anything that
  // originally sat AFTER the removed chunk shifts left by `count`.
  let insertIdx = to - 1;
  if (to > end) insertIdx -= count;
  insertIdx = Math.min(Math.max(0, insertIdx), rest.length);

  rest.splice(insertIdx, 0, ...chunk);
  return rest.join("\n");
}
